import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  getWatchers,
  toggleWatcher,
  deleteWatcher,
  duplicateWatcher,
  getPollingStatus,
  triggerPoll,
  type Watcher,
  type PollingStatus,
} from '../lib/api';

const CABIN_LABELS: Record<string, string> = {
  economy: 'Economy',
  premium_economy: 'Premium Economy',
  business: 'Business',
  first: 'First',
};

function WatcherCard({ watcher, onToggle, onDelete, onDuplicate }: {
  watcher: Watcher;
  onToggle: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
}) {
  const [deleting, setDeleting] = useState(false);
  const [duplicating, setDuplicating] = useState(false);

  const handleDelete = async () => {
    if (!confirm(`Delete watcher "${watcher.name}"?`)) return;
    setDeleting(true);
    await onDelete();
  };

  const handleDuplicate = async () => {
    setDuplicating(true);
    await onDuplicate();
    setDuplicating(false);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 md:p-6 mb-4">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <h3 className="text-base md:text-lg font-semibold">{watcher.name}</h3>
            <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${watcher.enabled ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
              {watcher.enabled ? 'Active' : 'Paused'}
            </span>
          </div>

          <div className="mt-2 text-gray-500 text-xs md:text-sm space-y-1">
            <div className="break-words">
              <strong>Route:</strong> {watcher.origins.slice(0, 3).join(', ')}
              {watcher.origins.length > 3 && ` +${watcher.origins.length - 3}`}
              {' → '}
              {watcher.destinations.slice(0, 3).join(', ')}
              {watcher.destinations.length > 3 && ` +${watcher.destinations.length - 3}`}
              {watcher.directOnly && <span className="ml-2 inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">Direct Only</span>}
            </div>
            <div>
              <strong>Cabins:</strong> {watcher.cabinClasses.map(c => CABIN_LABELS[c]).join(', ')}
            </div>
            <div>
              <strong>Programs:</strong> {watcher.programs.length > 0 ? watcher.programs.join(', ') : 'All'}
            </div>
            <div>
              <strong>Dates:</strong> {watcher.dateStart} to {watcher.dateEnd}
              {watcher.minSeats > 1 && (
                <span className="ml-2 md:ml-4">
                  <strong>Min Seats:</strong> {watcher.minSeats}
                </span>
              )}
            </div>
            {/* Price limits */}
            {(Object.keys(watcher.maxPoints).length > 0 || Object.keys(watcher.maxPriceUsd).length > 0) && (
              <div className="break-words">
                <strong>Limits:</strong>{' '}
                {watcher.cabinClasses.map(cabin => {
                  const maxPts = watcher.maxPoints[cabin];
                  const maxPrice = watcher.maxPriceUsd[cabin];
                  if (!maxPts && !maxPrice) return null;
                  return (
                    <span key={cabin} className="mr-2 md:mr-3">
                      {CABIN_LABELS[cabin]}: {maxPts ? `${maxPts.toLocaleString()} pts` : ''}
                      {maxPts && maxPrice ? ' / ' : ''}
                      {maxPrice ? `$${maxPrice.toLocaleString()}` : ''}
                    </span>
                  );
                })}
              </div>
            )}
            {/* Excluded airlines */}
            {watcher.excludeAirlines.length > 0 && (
              <div className="flex flex-wrap items-center gap-1">
                <strong>Exclude:</strong>
                {watcher.excludeAirlines.map(code => (
                  <span key={code} className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-rose-100 text-rose-700">
                    {code}
                  </span>
                ))}
              </div>
            )}
            {/* Notifications */}
            {watcher.notifications.length > 0 && watcher.notifications[0]?.type === 'email' && (
              <div className="break-all">
                <strong>Notify:</strong>{' '}
                {(watcher.notifications[0]?.emails ?? []).join(', ')}
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-2 shrink-0">
          <label className="switch">
            <input
              type="checkbox"
              checked={watcher.enabled}
              onChange={onToggle}
            />
            <span className="switch-slider" />
          </label>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Link to={`/watcher/${watcher.id}`} className="inline-flex items-center justify-center px-3 md:px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors no-underline">
          Edit
        </Link>
        <button
          onClick={handleDuplicate}
          className="inline-flex items-center justify-center px-3 md:px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors cursor-pointer border-none disabled:opacity-50"
          disabled={duplicating}
        >
          {duplicating ? '...' : 'Duplicate'}
        </button>
        <button
          onClick={handleDelete}
          className="inline-flex items-center justify-center px-3 md:px-4 py-2 text-sm font-medium rounded-lg bg-rose-600 text-white hover:bg-rose-700 transition-colors cursor-pointer border-none disabled:opacity-50"
          disabled={deleting}
        >
          {deleting ? '...' : 'Delete'}
        </button>
      </div>
    </div>
  );
}

function PollingStatusCard({ status, onTrigger }: {
  status: PollingStatus | null;
  onTrigger: () => void;
}) {
  const [triggering, setTriggering] = useState(false);

  const handleTrigger = async () => {
    setTriggering(true);
    await onTrigger();
    setTimeout(() => setTriggering(false), 2000);
  };

  if (!status) return null;

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 md:p-6 mb-6 md:mb-8">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h3 className="font-semibold mb-2">Polling Status</h3>
          <div className="text-xs md:text-sm text-gray-500 flex flex-wrap items-center gap-2 md:gap-4">
            <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${status.isRunning ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
              {status.isRunning ? 'Running' : 'Stopped'}
            </span>
            {status.isPolling && (
              <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">
                Polling...
              </span>
            )}
            <span>Interval: {status.intervalMinutes}m</span>
            {status.lastPollTime && (
              <span>Last: {new Date(status.lastPollTime).toLocaleTimeString()}</span>
            )}
          </div>
        </div>
        <button
          onClick={handleTrigger}
          className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg bg-slate-700 text-white hover:bg-slate-800 transition-colors cursor-pointer border-none disabled:opacity-50 self-start sm:self-auto"
          disabled={triggering || status.isPolling}
        >
          {triggering ? 'Triggered!' : 'Poll Now'}
        </button>
      </div>
    </div>
  );
}

export function DashboardPage() {
  const [watchers, setWatchers] = useState<Watcher[]>([]);
  const [pollingStatus, setPollingStatus] = useState<PollingStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    try {
      const [watchersData, statusData] = await Promise.all([
        getWatchers(),
        getPollingStatus(),
      ]);
      setWatchers(watchersData);
      setPollingStatus(statusData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();

    // Refresh polling status every 30 seconds
    const interval = setInterval(() => {
      getPollingStatus().then(setPollingStatus).catch(console.error);
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleToggle = async (id: string) => {
    try {
      const updated = await toggleWatcher(id);
      setWatchers(watchers.map(w => w.id === id ? updated : w));
    } catch (error) {
      console.error('Failed to toggle watcher:', error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteWatcher(id);
      setWatchers(watchers.filter(w => w.id !== id));
    } catch (error) {
      console.error('Failed to delete watcher:', error);
    }
  };

  const handleDuplicate = async (id: string) => {
    try {
      const newWatcher = await duplicateWatcher(id);
      setWatchers([newWatcher, ...watchers]);
    } catch (error) {
      console.error('Failed to duplicate watcher:', error);
    }
  };

  const handleTriggerPoll = async () => {
    try {
      await triggerPoll();
      // Refresh status after a short delay
      setTimeout(() => {
        getPollingStatus().then(setPollingStatus).catch(console.error);
      }, 1000);
    } catch (error) {
      console.error('Failed to trigger poll:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <div className="w-8 h-8 border-2 border-gray-200 border-t-slate-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-6 md:mb-8">
        <h2 className="text-xl md:text-2xl font-bold">Watchers</h2>
        <Link to="/watcher/new" className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg bg-slate-700 text-white hover:bg-slate-800 transition-colors no-underline self-start sm:self-auto">
          + New Watcher
        </Link>
      </div>

      <PollingStatusCard status={pollingStatus} onTrigger={handleTriggerPoll} />

      {watchers.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-8 md:p-12 text-center">
          <p className="text-gray-500 mb-4">No watchers configured yet.</p>
          <Link to="/watcher/new" className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg bg-slate-700 text-white hover:bg-slate-800 transition-colors no-underline">
            Create your first watcher
          </Link>
        </div>
      ) : (
        watchers.map(watcher => (
          <WatcherCard
            key={watcher.id}
            watcher={watcher}
            onToggle={() => handleToggle(watcher.id)}
            onDelete={() => handleDelete(watcher.id)}
            onDuplicate={() => handleDuplicate(watcher.id)}
          />
        ))
      )}
    </div>
  );
}
